import { type FC, useState, useEffect, useMemo } from "react";
import { Input, Tooltip, Form } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType, SorterResult } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { BRAND_ICONS_URLS, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { useManualActionsFlags } from "../../../hooks/useFeatureFlag";
import styles from "./AllManualActions.module.css";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import Select from "../../shared/Select/Select";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import { isEmailAddress } from "../../../utils/adminAuthUtils";
import { secondsToHms } from "../../../utils/secondsToHms";
import { formatDurationSeconds } from "../../../utils/formatDurationSeconds";
import { convertToUserTimezone } from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import {
  AiActionValue,
  AiColumnTitle,
} from "../../shared/AiAction/AiAction";
import {
  useGetAALAllTable,
  type AllAALTableFilters,
  type AllAALTableItem,
  type AllAALApvTableItem,
} from "../../../queries/agent-audit-log/all/useGetAALAllTable";
import {
  cascadingOptionsFor,
  filterOptionsFor,
} from "../../../queries/agent-audit-log/aalV2";
import { useDownloadAALAllTable } from "../../../queries/agent-audit-log/all/useDownloadAALAllTable";
import { useDownloadOnTrigger } from "../../../hooks/useDownloadOnTrigger";
import { useGetAALWalletKPIs } from "../../../queries/agent-audit-log/wallet/useGetAALWalletKPIs";
import { useGetAALSuspensionsKPIs } from "../../../queries/agent-audit-log/suspensions/useGetAALSuspensionsKPIs";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";
import { useGetAALPromosKPIs } from "../../../queries/agent-audit-log/promos/useGetAALPromosKPIs";
import { useGetAALSportsBetsKPIs } from "../../../queries/agent-audit-log/sports-bets/useGetAALSportsBetsKPIs";
import { useGetAALWithdrawalsKPIs } from "../../../queries/agent-audit-log/withdrawals/useGetAALWithdrawalsKPIs";

import {
  type AllManualActionsPanelFilters,
  buildActionTypeOptions,
  buildSectionOptions,
  hasAppliedAllManualActionsFilters,
  isAllManualActionsPanelDirty,
} from "./allManualActionsPanelUtils";
import { toAalSort } from "../manualActionsTableShared";

// CustomAutoCompleteControlled is a JS component — cast to avoid incorrect prop inference
type AutoCompleteOption = { value: string; label: string; id: string };
type CustomAutoCompleteProps = {
  allOptions: AutoCompleteOption[];
  selector: string;
  value: string;
  handleChange: (val: string) => void;
  clearOptions: () => void;
  placeholder?: string;
  onSearchChange?: (val: string) => void;
};
const TypedAutoComplete =
  CustomAutoCompleteControlled as unknown as FC<CustomAutoCompleteProps>;

// DataTable is a JS component — cast to a typed FC to avoid incorrect prop inference
type DataTableProps = {
  columns: ColumnsType<AllAALTableItem>;
  dataSource: AllAALTableItem[];
  loading: boolean;
  onChange: (
    p: unknown,
    f: unknown,
    s: SorterResult<AllAALTableItem> | SorterResult<AllAALTableItem>[],
  ) => void;
  rowKey: (record: AllAALTableItem) => string;
  scroll: { x: number; y: number };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, size: number) => void;
  };
};
const TypedDataTable = DataTable as unknown as FC<DataTableProps>;

type Role = {
  id: string;
  name: string;
  color: string;
  is_system_role: boolean;
  has_all_permissions: boolean;
  in_use: boolean;
  created_at: string;
  updated_at: string;
  actions: string[];
  permissions: string[];
};

interface AllManualActionsProps {
  activeTab?: string;
  agentPerformanceView?: boolean;
  showFilters?: boolean;
  showKpis?: boolean;
  dates?: [Dayjs, Dayjs] | null;
  downloadTrigger?: number;
}

const INITIAL_PANEL_FILTERS: AllManualActionsPanelFilters = {
  action_type: null,
  agent: "",
  role: null,
  section: null,
  source_ip: "",
  username: "",
};

const BLOCKED_IP_ACTION_TYPE = "PAM_ACCESS_BLOCKED_IP";

/**
 * Maps Ant Design column sort `field` to backend `sort_by`.
 * Detail table: `created_at` (the "Date&Time" column) → `date`.
 */
function allManualActionsSortFieldForApi(field: string): string {
  if (field === "created_at") return "date";
  return field;
}

function getAllManualActionsRowKey(record: AllAALTableItem): string {
  return (
    record.event_id ||
    record.event_sort_key ||
    [record.created_at, record.action_type].join("|")
  );
}

function AllManualActions({
  activeTab: _activeTab = "all",
  agentPerformanceView = false,
  showFilters = false,
  showKpis = false,
  dates,
  downloadTrigger = 0,
}: AllManualActionsProps) {
  // Filter form — values here are drafts until the user clicks Apply
  const [form] = Form.useForm();
  const watchedAgent = Form.useWatch("agent", form) ?? "";
  const watchedActionType = Form.useWatch("action_type", form) as
    | string
    | null
    | undefined;
  const watchedRole = Form.useWatch("role", form);
  const watchedSection = Form.useWatch("section", form) as
    | string
    | null
    | undefined;
  const watchedUsername = Form.useWatch("username", form);
  // Bump this to force-remount the autocomplete when clearing filters
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [sourceIpDraft, setSourceIpDraft] = useState("");
  // Form.useWatch can't see agent changes (no Form.Item), so we track it separately
  const [isAgentDirty, setIsAgentDirty] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<AllManualActionsPanelFilters>(INITIAL_PANEL_FILTERS);

  const isDirty = isAllManualActionsPanelDirty(
    {
      action_type: watchedActionType,
      role: watchedRole,
      section: watchedSection,
      source_ip: sourceIpDraft,
      username: watchedUsername,
    },
    appliedFilters,
    isAgentDirty,
  );
  const hasAppliedFilters = hasAppliedAllManualActionsFilters(appliedFilters);

  const { showCasinoKpi, showPikkemKpi, showSportsbookKpi } =
    useManualActionsFlags();

  // APV role — changes here refetch the table right away (no Apply needed)
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);

  const start_date = dates?.[0]?.utc().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const tableFilters: AllAALTableFilters = {
    view: listView,
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    page: currentPage,
    size: pageSize,
    sort: toAalSort(allManualActionsSortFieldForApi(sortField ?? ""), sortOrder),
    ...(agentPerformanceView
      ? selectedRole.length > 0 && { agent_role_id: selectedRole[0] }
      : appliedFilters.role && { agent_role_id: appliedFilters.role }),
    ...(!agentPerformanceView &&
      appliedFilters.action_type && {
        action_type: appliedFilters.action_type,
      }),
    ...(!agentPerformanceView &&
      appliedFilters.agent && { agent_id: appliedFilters.agent }),
    ...(!agentPerformanceView &&
      appliedFilters.section && {
        event_category: appliedFilters.section,
      }),
    ...(!agentPerformanceView &&
      appliedFilters.source_ip && { source_ip: appliedFilters.source_ip }),
    ...(!agentPerformanceView &&
      appliedFilters.username && { username: appliedFilters.username }),
  };

  // KPIs only care about the date range
  const kpiFilters = {
    start_date: start_date ?? "",
    end_date: end_date ?? "",
  };

  const {
    items: tableItems,
    total: totalResults,
    filters: tableFiltersMeta,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALAllTable(tableFilters);

  const sectionOptions = useMemo(
    () =>
      buildSectionOptions(
        filterOptionsFor(tableFiltersMeta, "event_category"),
        watchedSection ?? appliedFilters.section,
      ),
    [tableFiltersMeta, watchedSection, appliedFilters.section],
  );

  const actionTypeAllowedValues = watchedSection
    ? cascadingOptionsFor(tableFiltersMeta, "action_type")[watchedSection]
    : null;

  const actionTypeOptions = useMemo(
    () =>
      buildActionTypeOptions(
        filterOptionsFor(tableFiltersMeta, "action_type"),
        watchedActionType ?? appliedFilters.action_type,
        actionTypeAllowedValues,
      ),
    [
      tableFiltersMeta,
      watchedActionType,
      appliedFilters.action_type,
      actionTypeAllowedValues,
    ],
  );

  useEffect(() => {
    if (!watchedActionType) return;
    const allowed = new Set(
      buildActionTypeOptions(
        filterOptionsFor(tableFiltersMeta, "action_type"),
        null,
        actionTypeAllowedValues,
      ).map((option) => option.value),
    );
    if (!allowed.has(watchedActionType)) {
      form.setFieldValue("action_type", null);
    }
  }, [tableFiltersMeta, watchedActionType, actionTypeAllowedValues, form]);

  const { data: walletKpiData, isLoading: isWalletKpiLoading } =
    useGetAALWalletKPIs(kpiFilters);
  const { data: suspensionsKpiData, isLoading: isSuspensionsKpiLoading } =
    useGetAALSuspensionsKPIs(kpiFilters);
  const { data: promoKpiData, isLoading: isPromoKpiLoading } =
    useGetAALPromosKPIs(kpiFilters);
  const { data: sportsbetKpiData, isLoading: isSportsbetKpiLoading } =
    useGetAALSportsBetsKPIs(kpiFilters);
  const { data: withdrawalsKpiData, isLoading: isWithdrawalsKpiLoading } =
    useGetAALWithdrawalsKPIs(kpiFilters);
  const { data: roles } = useGetRoles() as { data: Role[] | undefined };
  const { data: staffData } = useGetStaff({
    page_size: 100,
  });

  const agentOptions = (staffData?.items ?? []).map((s) => ({
    value: s.email,
    label: s.email,
    id: s.id,
  }));
  const roleOptions = (roles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  // Quick lookup: role ID → { name, color } for the Role column
  const roleById = Object.fromEntries(
    (roles ?? []).map((r) => [
      r.id,
      { name: r.name, color: r.color ?? "#6b7280" },
    ]),
  );

  // Go back to page 1 when the APV role changes
  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
  }, [selectedRole, agentPerformanceView]);

  // Clean up when toggling between normal view and APV
  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, [agentPerformanceView]);

  // Download — fires when the parent bumps downloadTrigger
  const { start: downloadTable } = useDownloadAALAllTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(agentPerformanceView
      ? selectedRole.length > 0
        ? { agent_role_id: selectedRole[0] }
        : {}
      : {
          ...(appliedFilters.action_type && {
            action_type: appliedFilters.action_type,
          }),
          ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
          ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
          ...(appliedFilters.section && {
            event_category: appliedFilters.section,
          }),
          ...(appliedFilters.source_ip && {
            source_ip: appliedFilters.source_ip,
          }),
          ...(appliedFilters.username && {
            username: appliedFilters.username,
          }),
        }),
  }));

  const totalRebetCash = parseFloat(walletKpiData?.rebet_cash_delta ?? "0");
  const totalCasinoCash = parseFloat(walletKpiData?.casino_cash_delta ?? "0");
  const totalPikkemCash = parseFloat(walletKpiData?.p_cash_delta ?? "0");

  const handleClearFilters = () => {
    form.resetFields();
    form.setFieldsValue({
      agent: "",
      section: null,
      action_type: null,
    });
    // Remount the autocomplete so it fully resets
    setAutoCompleteKey((k) => k + 1);
    setSourceIpDraft("");
    setIsAgentDirty(false);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  };

  const handleApply = () => {
    const values = form.getFieldsValue();
    const sourceIp = sourceIpDraft.trim();
    setSourceIpDraft(sourceIp);
    setAppliedFilters({
      action_type: values.action_type ?? null,
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      section: values.section ?? null,
      source_ip: sourceIp,
      username: values.username ?? "",
    });
    setIsAgentDirty(false);
    setCurrentPage(1);
  };

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: SorterResult<AllAALTableItem> | SorterResult<AllAALTableItem>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s.field) {
      setSortField(s.field as string);
      setSortOrder(s.order ?? null);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const formatCurrency = (value: number | string, percentage = false) =>
    new Intl.NumberFormat("en-US", {
      style: percentage ? "percent" : "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));

  const renderKpiTooltip = (delta: number) => (
    <div style={{ padding: "4px 8px", fontFamily: "Poppins-regular" }}>
      <div style={{ marginBottom: "8px" }}>
        <div style={{ fontSize: "12px", textTransform: "uppercase" }}>
          {AppStrings.DELTA_SYMBOL} Total
        </div>
        <div
          style={{
            color: delta >= 0 ? "#71BF8A" : "#E74C3C",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {formatCurrency(delta)}
        </div>
      </div>
    </div>
  );

  const renderPercent = (percent: number) => {
    const sign = percent >= 0 ? "+" : "";
    return (
      <span
        style={{
          color: getColorBasedOnValue(percent),
          fontSize: "11px",
          marginLeft: "4px",
        }}
      >
        {sign}
        {percent.toFixed(1)}%
      </span>
    );
  };

  const columns: ColumnsType<AllAALTableItem> = [
    {
      title: AppStrings.DATE_AND_TIME,
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      showSorterTooltip: false,
      width: 180,
      render: (text: string) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.SECTION,
      dataIndex: "event_category",
      key: "event_category",
      width: 140,
      render: (value: string) => <span>{value || "—"}</span>,
    },
    {
      title: AppStrings.ACTION_TYPE,
      dataIndex: "action_type",
      key: "action_type",
      width: 200,
      render: (value: string) =>
        value === BLOCKED_IP_ACTION_TYPE ? AppStrings.PAM_ACCESS_BLOCKED_IP
        : value || "—",
    },
    {
      title: <AiColumnTitle />,
      dataIndex: "event_category",
      key: "ai",
      width: 100,
      render: (value: string) => <AiActionValue eventCategory={value} />,
    },
    {
      title: AppStrings.PAM_STAFF_IP_ADDRESS,
      dataIndex: "source_ip",
      key: "source_ip",
      width: 160,
      render: (value: string | null | undefined) => value || "—",
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 150,
      render: (value: string | null | undefined) => {
        if (!value) return <span>-</span>;
        const role = roleById[value];
        if (!role) return <span>{value || "-"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      showSorterTooltip: false,
      width: 180,
      render: (text: string | null | undefined, record) => {
        if (
          record.action_type === BLOCKED_IP_ACTION_TYPE ||
          !isEmailAddress(text) ||
          !record.agent_id
        ) {
          return <span>-</span>;
        }
        return (
          <a
            href={getManualActionsStaffManagementHref(record.agent_id)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.allManualActionsHoverable}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "target_username",
      key: "target_username",
      width: 150,
      render: (text: string | null | undefined, record) => {
        if (
          record.action_type === "AGENT_SIGNED_IN" ||
          record.action_type === "AGENT_SIGNED_OUT" ||
          record.action_type === BLOCKED_IP_ACTION_TYPE ||
          !text
        ) {
          return <span>-</span>;
        }
        return (
          <a
            href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.target_user_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.allManualActionsHoverable}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: AppStrings.REASON,
      dataIndex: "reason",
      key: "reason",
      width: 300,
      render: (text: string) => text || "-",
    },
  ];

  // Agent Performance View columns
  const agentPerformanceColumns: ColumnsType<AllAALApvTableItem> = [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 200,
      render: (text: string) => {
        if (!isEmailAddress(text)) {
          return <span>-</span>;
        }
        return (
          <a
            href={getManualActionsStaffManagementHref()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.allManualActionsHoverable}
          >
            {text}
          </a>
        );
      },
    },
    // -- Wallet --
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {`${AppStrings.DELTA_SYMBOL} ${AppStrings.PRIMARY_CURRENCY}`}
          <Tooltip title={AppStrings.REBET_CASH_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "rebet_cash_delta",
      key: "rebet_cash_delta",
      width: 180,
      sorter: true,
      render: (text: number, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["rebet_cash_pct_change"] != null &&
            renderPercent(record["rebet_cash_pct_change"] as number)}
        </span>
      ),
    },
    ...(showCasinoKpi
      ? [
          {
            title: (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {`${AppStrings.DELTA_SYMBOL} ${AppStrings.TOTAL_CASINO_CASH}`}
                <Tooltip title={AppStrings.CASINO_CASH_TOOLTIP}>
                  <InfoCircleOutlined
                    style={{
                      color: "var(--grey-400)",
                      fontSize: "12px",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </span>
            ),
            dataIndex: "casino_cash_delta",
            key: "casino_cash_delta",
            width: 180,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatCurrency((text as number) || 0)}
                {record["casino_cash_pct_change"] != null &&
                  renderPercent(record["casino_cash_pct_change"] as number)}
              </span>
            ),
          },
        ]
      : []),
    ...(showPikkemKpi
      ? [
          {
            title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.PIKKEM_CASH}`,
            dataIndex: "p_cash_delta",
            key: "p_cash_delta",
            width: 180,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatCurrency((text as number) || 0)}
                {record["p_cash_pct_change"] != null &&
                  renderPercent(record["p_cash_pct_change"] as number)}
              </span>
            ),
          },
        ]
      : []),
    // -- Withdrawals --
    {
      title: AppStrings.TOTAL_WITHDRAWAL_AMOUNT_APPROVED,
      dataIndex: "total_withdrawal_amount_reviewed",
      key: "total_withdrawal_amount_reviewed",
      width: 220,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["total_withdrawal_amount_reviewed_pct_change"] != null &&
            renderPercent(
              record["total_withdrawal_amount_reviewed_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_REVIEWED_SHORT,
      dataIndex: "withdrawals_reviewed_count",
      key: "withdrawals_reviewed_count",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["withdrawals_reviewed_count_pct_change"] != null &&
            renderPercent(
              record["withdrawals_reviewed_count_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_APPROVED,
      dataIndex: "withdrawals_approved_count",
      key: "withdrawals_approved_count",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["withdrawals_approved_count_pct_change"] != null &&
            renderPercent(
              record["withdrawals_approved_count_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_DECLINED,
      dataIndex: "withdrawals_declined_count",
      key: "withdrawals_declined_count",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["withdrawals_declined_count_pct_change"] != null &&
            renderPercent(
              record["withdrawals_declined_count_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: AppStrings.AVG_TIME_REQUEST_TO_DECISION,
      // dataIndex points at the legacy pre-formatted string so antd still
      // hands it to render() as `text`; the canonical seconds field is read
      // off the row inside render and takes precedence when present.
      dataIndex: "avg_time_request_to_decision",
      key: "avg_time_request_to_decision",
      width: 200,
      render: (text: string | undefined, record) => {
        const seconds = record.avg_request_to_decision_seconds;
        if (seconds != null) {
          return <span>{formatDurationSeconds(seconds)}</span>;
        }
        return <span>{text || "—"}</span>;
      },
    },
    {
      title: AppStrings.AVG_TIME_REVIEW_START_TO_DECISION,
      dataIndex: "avg_time_review_start_to_decision",
      key: "avg_time_review_start_to_decision",
      width: 220,
      render: (text: string | undefined, record) => {
        const seconds = record.avg_review_start_to_decision_seconds;
        if (seconds != null) {
          return <span>{formatDurationSeconds(seconds)}</span>;
        }
        return <span>{text || "—"}</span>;
      },
    },
    // -- Promos --
    // Line 1: Free Pick Amount, Free Picks - Realized Value, # of Free Picks
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {`${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_PICK_AMOUNT}`}
          <Tooltip title={AppStrings.FREE_PICKS_AMOUNT_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "promos_amount",
      key: "free_pick_amount_delta",
      width: 200,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["promos_amount_pct_change"] != null &&
            renderPercent(record["promos_amount_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {AppStrings.FREE_PICKS_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.FREE_PICKS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "promos_amount_realized",
      key: "free_picks_amount_realized",
      width: 200,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["promos_amount_realized_pct_change"] != null &&
            renderPercent(
              record["promos_amount_realized_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_PICKS}`,
      dataIndex: "promos_given",
      key: "free_picks_given",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["promos_given_pct_change"] != null &&
            renderPercent(record["promos_given_pct_change"] as number)}
        </span>
      ),
    },
    // Line 2: Profit Boosts - Realized Value, # of Profit Boosts
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {AppStrings.PROFIT_BOOST_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.PROFIT_BOOST_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "promos_amount_realized",
      key: "profit_boosts_amount_realized",
      width: 220,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["promos_amount_realized_pct_change"] != null &&
            renderPercent(
              record["promos_amount_realized_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_PROFIT_BOOSTS}`,
      dataIndex: "promos_given",
      key: "profit_boosts_given",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["promos_given_pct_change"] != null &&
            renderPercent(record["promos_given_pct_change"] as number)}
        </span>
      ),
    },
    // Line 3: Free Spins - Realized Value, # of Free Spins
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {AppStrings.FREE_SPINS_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.FREE_SPINS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "promos_amount_realized",
      key: "free_spins_amount_realized",
      width: 220,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["promos_amount_realized_pct_change"] != null &&
            renderPercent(
              record["promos_amount_realized_pct_change"] as number,
            )}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_SPINS}`,
      dataIndex: "promos_given",
      key: "free_spins_given",
      width: 180,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["promos_given_pct_change"] != null &&
            renderPercent(record["promos_given_pct_change"] as number)}
        </span>
      ),
    },
    // -- Sports Bets (gated by showSportsbookKpi) --
    ...(showSportsbookKpi
      ? [
          {
            title: AppStrings.TURNOVER,
            dataIndex: "bet_turnover",
            key: "bet_turnover",
            width: 180,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatCurrency((text as number) || 0)}
                {record["bet_turnover_pct_change"] != null &&
                  renderPercent(record["bet_turnover_pct_change"] as number)}
              </span>
            ),
          },
          {
            title: AppStrings.PROFIT_AND_LOSS,
            dataIndex: "bet_pnl",
            key: "bet_pnl",
            width: 180,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatCurrency((text as number) || 0)}
                {record["bet_pnl_pct_change"] != null &&
                  renderPercent(record["bet_pnl_pct_change"] as number)}
              </span>
            ),
          },
          {
            title: AppStrings.HASH_OF_BETS,
            dataIndex: "bets_settled_count",
            key: "bets_settled_count",
            width: 150,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span>
                {(text as number) ?? 0}
                {record["bets_settled_count_pct_change"] != null &&
                  renderPercent(
                    record["bets_settled_count_pct_change"] as number,
                  )}
              </span>
            ),
          },
          {
            title: (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {AppStrings.USER_REALIZED_VALUE}
                <Tooltip title={AppStrings.USER_REALIZED_VALUE_TOOLTIP}>
                  <InfoCircleOutlined
                    style={{
                      color: "var(--grey-400)",
                      fontSize: "12px",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </span>
            ),
            dataIndex: "bet_amount_realized",
            key: "bet_amount_realized",
            width: 200,
            sorter: true,
            render: (text: number, record: AllAALApvTableItem) => (
              <span>
                {(text as number) ?? 0}
                {record["bet_amount_realized_pct_change"] != null &&
                  renderPercent(
                    record["bet_amount_realized_pct_change"] as number,
                  )}
              </span>
            ),
          },
        ]
      : []),
    // -- Suspensions --
    {
      title: AppStrings.USERS_SUSPENDED,
      dataIndex: "users_suspended_delta",
      key: "users_suspended_delta",
      width: 160,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["users_suspended_pct_change"] != null &&
            renderPercent(record["users_suspended_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: AppStrings.USERS_UNSUSPENDED,
      dataIndex: "users_unsuspended_delta",
      key: "users_unsuspended_delta",
      width: 160,
      sorter: true,
      render: (text: number, record: AllAALApvTableItem) => (
        <span>
          {text ?? 0}
          {record["users_unsuspended_pct_change"] != null &&
            renderPercent(record["users_unsuspended_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 170,
      render: (value: string) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.allManualActionsContainer}>
      <Form form={form}>
        <div
          className={styles.allManualActionsSelectors}
          style={{
            display: showFilters && !agentPerformanceView ? "flex" : "none",
          }}
        >
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.SECTION}</span>
            <Form.Item name="section" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={sectionOptions}
                virtual={false}
              />
            </Form.Item>
          </div>
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.ACTION_TYPE}</span>
            <Form.Item name="action_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={actionTypeOptions}
                virtual={false}
              />
            </Form.Item>
          </div>
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.PAM_AGENT}</span>
            {/* CustomAutoCompleteControlled uses handleChange not onChange;
                manage its form value manually via form.setFieldValue */}
            <TypedAutoComplete
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={(id: string) => {
                form.setFieldValue("agent", id);
                setIsAgentDirty(true);
              }}
              clearOptions={() => form.setFieldValue("agent", "")}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.PAM_STAFF_IP_ADDRESS}</span>
            <Input
              value={sourceIpDraft}
              onChange={(event) => setSourceIpDraft(event.target.value)}
              placeholder={AppStrings.PAM_STAFF_IP_ADDRESS_PLACEHOLDER}
              className={styles.allManualActionsFilterInput}
            />
          </div>
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={roleOptions}
              />
            </Form.Item>
          </div>
          <div className={styles.allManualActionsSelector}>
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <Input
                placeholder="Text"
                className={styles.allManualActionsFilterInput}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className={styles.allManualActionsSelectorActionButtons}
        style={{
          display: showFilters && !agentPerformanceView ? "flex" : "none",
        }}
      >
        <button
          className={
            hasAppliedFilters
              ? `${styles.clear} ${styles.dirty}`
              : styles.clear
          }
          onClick={handleClearFilters}
        >
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          className={isDirty ? `${styles.apply} ${styles.dirty}` : styles.apply}
          onClick={handleApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>
      <div
        className={styles.allManualActionsKpiCards}
        style={{ display: showKpis && !agentPerformanceView ? "flex" : "none" }}
      >
        <div className={styles.kpiRow}>
          <div className={styles.kpiHeader}>{AppStrings.WALLET}</div>
          <div className={styles.kpiCardsContainer}>
            <div
              className={styles.kpiRowSeparator}
              style={{ position: "relative" }}
            >
              <Tooltip title={AppStrings.REBET_CASH_TOOLTIP}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <Tooltip
                title={renderKpiTooltip(totalRebetCash)}
                placement="top"
                color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
              >
                <div style={{ flex: 1 }}>
                  <KpiCard
                    title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.PRIMARY_CURRENCY}`}
                    value={totalRebetCash}
                    hugeValue={false}
                    percentage={walletKpiData?.rebet_cash_pct_change}
                    backgroundColor={KPI_CARD_BACKGROUND.DARK}
                    isApiRunning={isWalletKpiLoading}
                  />
                </div>
              </Tooltip>
            </div>
            {showCasinoKpi && (
              <div
                className={styles.kpiRowSeparator}
                style={{ position: "relative" }}
              >
                <Tooltip title={AppStrings.CASINO_CASH_TOOLTIP}>
                  <InfoCircleOutlined
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "var(--grey-400)",
                      fontSize: 14,
                      cursor: "help",
                      zIndex: 1,
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={renderKpiTooltip(totalCasinoCash)}
                  placement="top"
                  color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
                >
                  <div style={{ flex: 1 }}>
                    <KpiCard
                      title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.TOTAL_CASINO_CASH}`}
                      value={totalCasinoCash}
                      hugeValue={false}
                      percentage={walletKpiData?.casino_cash_pct_change}
                      backgroundColor={KPI_CARD_BACKGROUND.DARK}
                      isApiRunning={isWalletKpiLoading}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
            {showPikkemKpi && (
              <div className={styles.kpiRowSeparator}>
                <Tooltip
                  title={renderKpiTooltip(totalPikkemCash)}
                  placement="top"
                  color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
                >
                  <div style={{ flex: 1 }}>
                    <KpiCard
                      title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.PIKKEM_CASH}`}
                      value={totalPikkemCash}
                      hugeValue={false}
                      percentage={walletKpiData?.p_cash_pct_change}
                      backgroundColor={KPI_CARD_BACKGROUND.DARK}
                      isApiRunning={isWalletKpiLoading}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiHeader}>
            {AppStrings.WITHDRAWAL_REVIEW_TITLE}
          </div>
          <div className={styles.kpiCardsContainer}>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.TOTAL_WITHDRAWAL_AMOUNT_APPROVED}
                value={Number(
                  withdrawalsKpiData?.total_withdrawal_amount_reviewed ?? 0,
                )}
                hugeValue={false}
                percentage={
                  withdrawalsKpiData?.total_withdrawal_amount_reviewed_pct_change
                }
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.NUMBER_OF_WITHDRAWALS_REVIEWED_SHORT}
                value={Number(
                  withdrawalsKpiData?.withdrawals_reviewed_count ?? 0,
                )}
                hugeValue={false}
                percentage={
                  withdrawalsKpiData?.withdrawals_reviewed_count_pct_change
                }
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.NUMBER_OF_WITHDRAWALS_APPROVED}
                value={Number(
                  withdrawalsKpiData?.withdrawals_approved_count ?? 0,
                )}
                hugeValue={false}
                percentage={
                  withdrawalsKpiData?.withdrawals_approved_count_pct_change
                }
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
          </div>
          <div className={styles.kpiCardsContainer}>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.NUMBER_OF_WITHDRAWALS_DECLINED}
                value={Number(
                  withdrawalsKpiData?.withdrawals_declined_count ?? 0,
                )}
                hugeValue={false}
                percentage={
                  withdrawalsKpiData?.withdrawals_declined_count_pct_change
                }
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.AVG_TIME_REQUEST_TO_DECISION}
                value={secondsToHms(
                  withdrawalsKpiData?.avg_request_to_decision_seconds,
                )}
                hugeValue={false}
                percentage={undefined}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
                displayAsRawText
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.AVG_TIME_REVIEW_START_TO_DECISION}
                value={secondsToHms(
                  withdrawalsKpiData?.avg_review_start_to_decision_seconds,
                )}
                hugeValue={false}
                percentage={undefined}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isWithdrawalsKpiLoading}
                displayAsRawText
              />
            </div>
          </div>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiHeader}>{AppStrings.PROMOS}</div>
          {/* Line 1: Free Pick Amount, Free Picks - Realized Value, # of Free Picks */}
          <div className={styles.kpiCardsContainer}>
            <div
              className={styles.kpiRowSeparator}
              style={{ position: "relative" }}
            >
              <Tooltip title={AppStrings.FREE_PICKS_AMOUNT_TOOLTIP}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_PICK_AMOUNT}`}
                value={promoKpiData?.free_picks_amount}
                hugeValue={false}
                percentage={promoKpiData?.free_picks_amount_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
              />
            </div>
            <div
              className={styles.kpiRowSeparator}
              style={{ position: "relative" }}
            >
              <Tooltip title={formatString(AppStrings.FREE_PICKS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={AppStrings.FREE_PICKS_REALIZED_VALUE}
                value={promoKpiData?.free_picks_amount_realized}
                hugeValue={false}
                percentage={promoKpiData?.free_picks_amount_realized_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_PICKS}`}
                value={promoKpiData?.free_picks_given}
                hugeValue={false}
                percentage={promoKpiData?.free_picks_given_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
          </div>
          {/* Line 2: Profit Boosts - Realized Value, # of Profit Boosts */}
          <div className={styles.kpiCardsContainer}>
            <div
              className={styles.kpiRowSeparator}
              style={{ position: "relative" }}
            >
              <Tooltip
                title={formatString(AppStrings.PROFIT_BOOST_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}
              >
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={AppStrings.PROFIT_BOOST_REALIZED_VALUE}
                value={promoKpiData?.profit_boosts_amount_realized}
                hugeValue={false}
                percentage={
                  promoKpiData?.profit_boosts_amount_realized_pct_change
                }
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_PROFIT_BOOSTS}`}
                value={promoKpiData?.profit_boosts_given}
                hugeValue={false}
                percentage={promoKpiData?.profit_boosts_given_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
          </div>
          {/* Line 3: Free Spins - Realized Value, # of Free Spins */}
          <div className={styles.kpiCardsContainer}>
            <div
              className={styles.kpiRowSeparator}
              style={{ position: "relative" }}
            >
              <Tooltip title={formatString(AppStrings.FREE_SPINS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={AppStrings.FREE_SPINS_REALIZED_VALUE}
                value={promoKpiData?.free_spins_amount_realized}
                percentage={promoKpiData?.free_spins_amount_realized_pct_change}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_SPINS}`}
                value={promoKpiData?.free_spins_given}
                percentage={promoKpiData?.free_spins_given_pct_change}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isPromoKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
          </div>
        </div>
        {showSportsbookKpi && (
          <div className={styles.kpiRow}>
            <div className={styles.kpiHeader}>{AppStrings.SPORTS_BETS}</div>
            <div className={styles.kpiCardsContainer}>
              <div className={styles.kpiRowSeparator}>
                <KpiCard
                  title={AppStrings.TURNOVER}
                  value={sportsbetKpiData?.turnover}
                  hugeValue={false}
                  percentage={sportsbetKpiData?.turnover_pct_change}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isSportsbetKpiLoading}
                />
              </div>
              <div className={styles.kpiRowSeparator}>
                <KpiCard
                  title={AppStrings.PROFIT_AND_LOSS}
                  value={sportsbetKpiData?.pnl}
                  hugeValue={false}
                  percentage={sportsbetKpiData?.pnl_pct_change}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isSportsbetKpiLoading}
                />
              </div>
              <div className={styles.kpiRowSeparator}>
                <KpiCard
                  title={AppStrings.HASH_OF_BETS}
                  value={sportsbetKpiData?.bets_settled_count}
                  hugeValue={false}
                  percentage={sportsbetKpiData?.bets_settled_count_pct_change}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isSportsbetKpiLoading}
                  maximumFractionDigits={0}
                />
              </div>
              <div
                className={styles.kpiRowSeparator}
                style={{ position: "relative" }}
              >
                <Tooltip title={AppStrings.USER_REALIZED_VALUE_TOOLTIP}>
                  <InfoCircleOutlined
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "var(--grey-400)",
                      fontSize: 14,
                      cursor: "help",
                      zIndex: 1,
                    }}
                  />
                </Tooltip>
                <KpiCard
                  title={AppStrings.USER_REALIZED_VALUE}
                  value={sportsbetKpiData?.amount_realized ?? 0}
                  hugeValue={false}
                  percentage={sportsbetKpiData?.amount_realized_pct_change}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isSportsbetKpiLoading}
                  maximumFractionDigits={2}
                />
              </div>
            </div>
          </div>
        )}
        <div className={styles.kpiRow}>
          <div className={styles.kpiHeader}>{AppStrings.SUSPENSIONS}</div>
          <div className={styles.kpiCardsContainer}>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.USERS_SUSPENDED}
                value={suspensionsKpiData?.users_suspended_delta}
                hugeValue={false}
                percentage={suspensionsKpiData?.users_suspended_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isSuspensionsKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
            <div className={styles.kpiRowSeparator}>
              <KpiCard
                title={AppStrings.USERS_UNSUSPENDED}
                value={suspensionsKpiData?.users_unsuspended_delta}
                hugeValue={false}
                percentage={suspensionsKpiData?.users_unsuspended_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isSuspensionsKpiLoading}
                maximumFractionDigits={0}
              />
            </div>
          </div>
        </div>
      </div>
      {agentPerformanceView ? (
        <AgentPerformanceView
          columns={agentPerformanceColumns}
          handleTableChange={handleTableChange}
          filteredData={tableItems as AllAALApvTableItem[]}
          roleOptions={roleOptions}
          selectedRoles={selectedRole}
          onRoleChange={setSelectedRole}
          scrollX={4500}
          isLoading={isTableLoading || isTableFetching}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            onChange: (page: number, size: number) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      ) : (
        <TypedDataTable
          columns={columns}
          dataSource={tableItems as AllAALTableItem[]}
          loading={isTableLoading || isTableFetching}
          onChange={handleTableChange}
          rowKey={getAllManualActionsRowKey}
          scroll={{ x: 1800, y: 500 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      )}
    </div>
  );
}

export default AllManualActions;
